import { LocalizationService } from '@abp/ng.core';
import { Component, inject, model, OnInit, output } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  conditionalValidator,
  DEFAULT_DATETIME_FORMAT_SHORT_DATE_PATTERN,
  DwApiSelectDirective,
  LanguageStore,
} from '@digiwall/core';
import {
  CurrentCompanyStore,
  GoliathCoreEnumerationTypeStore,
  SharedModule,
} from '@digiwall/goliath-core';
import {
  DocRole,
  DocTextService,
  PaymentTermLookupDto,
  PaymentTermService,
  StorageLocationLookupRequestDto,
  StorageLocationService,
} from '@digiwall/goliath-core/proxy';
import {
  CustomerInvoiceDto,
  CustomerInvoiceService,
  DayBookDto,
  DayBookService,
} from '@digiwall/goliath-documents/proxy';
import { DialogBoxComponent } from '@digiwall/ui-kit';
import { BehaviorSubject, map, Observable, of, tap } from 'rxjs';
import { format } from 'date-fns';
import moment from 'moment';
import { ToasterService } from '@abp/ng.theme.shared';
import {
  NgLabelTemplateDirective,
  NgOptionTemplateDirective,
  NgSelectComponent,
} from '@ng-select/ng-select';
import { EnumerationTypeDto } from '@digiwall/core/proxy';
import { StockFeatureDefinition } from '@digiwall/goliath-products/config';

export interface CustomerInvoiceCancellationResult {
  cancellationDocRole: DocRole;
  cancellationDocId: string;
}

const DISPLAY_NUMBER_TOKEN = '(%DisplayNumber)';
const DOCUMENT_DATE_TOKEN = '(%Date)';
@Component({
  selector: 'document-customer-invoice-cancellation',
  templateUrl: './customer-invoice-cancellation.component.html',
  imports: [
    DialogBoxComponent,
    SharedModule,
    ReactiveFormsModule,
    NgSelectComponent,
    NgLabelTemplateDirective,
    NgOptionTemplateDirective,
    DwApiSelectDirective,
  ],
})
export class CustomerInvoiceCancellationComponent implements OnInit {
  protected coreEnumationTypeStore = inject(GoliathCoreEnumerationTypeStore);
  private currentCompanyStore = inject(CurrentCompanyStore);
  languageStore = inject(LanguageStore);

  customerInvoice = model<CustomerInvoiceDto>();
  hasBeenCancelledByRole = output<CustomerInvoiceCancellationResult>();

  protected readonly stockFeature = inject(StockFeatureDefinition);

  protected customerInvoiceCancellationDocRoles: EnumerationTypeDto[];
  protected DocRole = DocRole;
  protected form: FormGroup;
  protected cancellationDocRoleControl: FormControl<string>;
  protected daybookControl: FormControl<string>;
  protected storageLocationControl: FormControl<string>;
  protected paymentTermControl: FormControl<string>;
  protected descriptionControl: FormControl<string>;
  protected daybooks$ = new BehaviorSubject<DayBookDto[]>([]);
  protected paymentTerms: PaymentTermLookupDto[];

  constructor(
    private formBuilder: FormBuilder,
    private daybookService: DayBookService,
    private paymentTermService: PaymentTermService,
    private storageLocationService: StorageLocationService,
    private invoiceService: CustomerInvoiceService,
    private localizationService: LocalizationService,
    private toaster: ToasterService,
    private docTextService: DocTextService,
  ) {
    this.getPaymentTerms();
  }

  ngOnInit(): void {
    this.initForm();

    this.daybookService.getAllDocRolesWithDayBook().subscribeOnce(res => {
      const docRoles = res.filter(x => x.hasDaybooks).map(x => x.docRole.toString());

      this.customerInvoiceCancellationDocRoles = this.coreEnumationTypeStore
        .customerInvoiceCancellationDocRoles()
        ?.filter(role => docRoles?.includes(role.id));
    });
    this.daybooks$.subscribe(daybooks => {
      this.daybookControl.patchValue(daybooks[0]?.id ?? null);
    });

    this.getDaybooks(this.cancellationDocRoleControl.value as DocRole);

    this.cancellationDocRoleControl.valueChanges.subscribe((newDocRole: DocRole) => {
      this.updateStorageLocationControlValidators(newDocRole);
      this.getDaybooks(newDocRole);
    });
  }

  cancelInvoice() {
    this.form.markAllAsTouched();
    if (!this.form.valid) {
      return;
    }

    const invoiceCancellationRequest = this.getInvoiceCancellationRequest();

    this.invoiceService
      .cancelInvoice(invoiceCancellationRequest)
      .subscribeOnce(cancellationResult => {
        this.toaster.success('Doc::CustomerInvoice:CancellationDone');
        this.hasBeenCancelledByRole.emit({
          cancellationDocRole: invoiceCancellationRequest.cancellationDocRole as DocRole,
          cancellationDocId: cancellationResult.cancellationDocId,
        });
      });
  }

  protected storageLocationSelect = (dto: StorageLocationLookupRequestDto) => {
    dto.sorting = [{ property: 'name' }];
    dto.queryProperties = ['code', 'name'];
    dto.companySiteId = this.customerInvoice().companySiteId;
    dto.onlyActive = true;
    return this.storageLocationService.getLookupList(dto);
  };

  private getPaymentTerms() {
    this.paymentTermService
      .getPaymentTermLookup()
      .subscribeOnce(paymentTerms => (this.paymentTerms = paymentTerms.items));
  }

  private initForm() {
    this.initCancellationDocRoleControl();
    this.initDaybookControl();
    this.initStorageLocationControl();
    this.initPaymentTermControl();

    this.initDescriptionControl().subscribeOnce(() => {
      this.form = this.formBuilder.group({
        cancellationDocRole: this.cancellationDocRoleControl,
        daybookId: this.daybookControl,
        storageLocationId: this.storageLocationControl,
        description: this.descriptionControl,
        paymentTermId: this.paymentTermControl,
      });
    });
  }

  private getDaybooks(docRole: DocRole) {
    this.daybookService
      .getDayBooksByRole(docRole)
      .subscribeOnce(daybooks => this.daybooks$.next(daybooks));
  }

  private updateStorageLocationControlValidators(newDocRole: DocRole) {
    if (newDocRole == DocRole.CustomerReturnNote && this.stockFeature.isEnabled) {
      this.storageLocationControl.addValidators(Validators.required);
    } else {
      this.storageLocationControl.clearValidators();
    }
    this.storageLocationControl.updateValueAndValidity();
  }

  private getInvoiceCancellationRequest() {
    return Object.assign(
      {
        invoiceId: this.customerInvoice().id,
      },
      this.form.getRawValue(),
    );
  }

  private initCancellationDocRoleControl() {
    this.cancellationDocRoleControl = this.formBuilder.control(
      this.currentCompanyStore.currentCompany().documentsConfiguration
        .defaultDocRoleForInvoiceCancellation,
      [Validators.required],
    );
  }

  private initDaybookControl() {
    this.daybookControl = this.formBuilder.control(null, [Validators.required]);
  }

  private initStorageLocationControl() {
    this.storageLocationControl = this.formBuilder.control(null, [
      conditionalValidator(() => {
        return (
          this.cancellationDocRoleControl.value == DocRole.CustomerReturnNote &&
          this.stockFeature.isEnabled
        );
      }, Validators.required),
    ]);
  }

  private initPaymentTermControl() {
    let defaultPaymentTermId =
      this.currentCompanyStore.currentCompany()?.documentsConfiguration?.paymentTermId ??
      this.customerInvoice().paymentTermId;

    this.paymentTermControl = this.formBuilder.control(defaultPaymentTermId, [Validators.required]);
  }

  private initDescriptionControl(): Observable<void> {
    const cancellationTextId =
      this.currentCompanyStore.currentCompany()?.documentsConfiguration?.cancelInvoiceTextId;

    let languageCode = this.languageStore.getTranslatedLanguageCodeByLanguageId()(
      this.customerInvoice().languageId,
    );
    let cancellationText$ =
      cancellationTextId == null
        ? of(
            this.localizationService.instant(
              'Doc::CustomerInvoice:CancellationDocDefaultDescription',
              this.customerInvoice().displayNumber,
              format(
                moment(this.customerInvoice().documentDate).toDate(),
                DEFAULT_DATETIME_FORMAT_SHORT_DATE_PATTERN,
              ),
            ),
          )
        : this.docTextService
            .get(cancellationTextId)
            .pipe(
              map(docText =>
                docText.text.allTranslations[languageCode || 'fr']
                  .replace(DISPLAY_NUMBER_TOKEN, this.customerInvoice().displayNumber)
                  .replace(
                    DOCUMENT_DATE_TOKEN,
                    format(
                      moment(this.customerInvoice().documentDate).toDate(),
                      DEFAULT_DATETIME_FORMAT_SHORT_DATE_PATTERN,
                    ),
                  ),
              ),
            );

    return cancellationText$.pipe(
      tap(text => {
        this.descriptionControl = this.formBuilder.control(text, [Validators.required]);
      }),
      map(() => void 0), // return void
    );
  }
}
